import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useUIStore } from "@/stores/useUIStore";
import { cn } from "@/lib/utils";

// DEV-824: pt belongs here alongside en/es. The store, the locale files
// and the content translations all carry three languages; a two-option switch
// was the only thing making the Portuguese half unreachable.
const OPTIONS = ["en", "es", "pt"] as const;

/**
 * EN / ES / PT toggle. Purely presentational — it only writes to the UI store;
 * LanguageProvider owns the i18next/DOM/localStorage side effects.
 *
 * `ghost` is the chrome variant (public navbar, app top bar), `solid` is the
 * filled variant used inside the profile settings card.
 */
function LanguageSwitcher({
  variant = "ghost",
  className,
}: {
  variant?: "ghost" | "solid";
  className?: string;
}) {
  const { t } = useTranslation();
  const language = useUIStore((s) => s.language);
  const setLanguage = useUIStore((s) => s.setLanguage);

  return (
    <div
      role="group"
      aria-label={t("profile.language")}
      className={cn(
        "flex items-center",
        variant === "ghost" ? "gap-0.5 text-xs sm:gap-1 sm:text-sm" : "gap-1",
        className,
      )}
    >
      {OPTIONS.map((option, index) => {
        const active = language === option;
        return (
          <Fragment key={option}>
            {variant === "ghost" && index > 0 && (
              <span aria-hidden="true" className="text-text-muted">
                |
              </span>
            )}
            <button
              type="button"
              onClick={() => setLanguage(option)}
              aria-pressed={active}
              className={cn(
                "transition-colors",
                variant === "ghost"
                  ? cn(
                      "rounded-chip px-1.5 py-0.5 sm:px-2",
                      active
                        ? "bg-surface-tertiary font-medium text-text-primary"
                        : "text-text-tertiary hover:text-text-secondary",
                    )
                  : cn(
                      "rounded-md px-2.5 py-1 text-xs font-medium",
                      active
                        ? "bg-brand-500 text-white"
                        : "bg-surface-tertiary text-text-secondary hover:bg-skeleton",
                    ),
              )}
            >
              {option.toUpperCase()}
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}

export { LanguageSwitcher };
